import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Backdrop,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useParams, useNavigate } from 'react-router-dom';
import { verifyTokenEmail } from '../../Model/Login';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  const isTokenFormatValid = (token) => {
    return token && token.length >= 30;
  };

  useEffect(() => {
    const validateToken = async () => {
      if (!isTokenFormatValid(token)) {
        setStatus('error');
        setMessage('Invalid or missing token.');
        setIsLoading(false);
        return;
      }

      try {
        const res = await verifyTokenEmail(token);
        if (res.success) {
          setStatus('success');
          setMessage('Your email has been verified successfully.');
        } else {
          setStatus('error');
          setMessage(res.msg || 'The verification link has expired or is invalid.');
        }
      } catch (err) {
        console.error(err);
        setStatus('error');
        setMessage('Something went wrong while verifying your email.');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setStatus('error');
      setMessage('No token found.');
      setIsLoading(false);
    }
  }, [token]);

  return (
    <Container maxWidth="sm">
      <Box mt={10} display="flex" justifyContent="center" alignItems="center">
        <Card
          sx={{
            width: '100%',
            textAlign: 'center',
            padding: 4,
            boxShadow: 4,
            borderRadius: 3,
            backgroundColor: '#fafafa',
          }}
        >
          <CardContent>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 4 }}
              color="primary"
            >
              Email Verification
            </Typography>

            {status === 'success' && (
              <Stack spacing={2} alignItems="center">
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 70 }} />
                <Typography variant="h6" fontWeight={500}>
                  Email Verified Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {message}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ mt: 2 }}
                >
                  Go to Login
                </Button>
              </Stack>
            )}

            {status === 'error' && (
              <Stack spacing={2} alignItems="center">
                <ErrorOutlineIcon color="error" sx={{ fontSize: 70 }} />
                <Typography variant="h6" fontWeight={500}>
                  Verification Failed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {message}
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ mt: 2 }}
                >
                  Back to Login
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backdropFilter: 'blur(4px)',
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default EmailVerificationPage;
